import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { authAPI } from '../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const LENGTH = 6;

/**
 * Six boxes for a code: typing moves on, backspace moves back, and a pasted
 * or autofilled code fills them all. Calls onComplete with the whole code.
 */
export function CodeInput({ onComplete, disabled, autoFocus = true, label = 'Code from your authenticator app' }) {
  const [digits, setDigits] = useState(Array(LENGTH).fill(''));
  const boxes = useRef([]);

  useEffect(() => { if (autoFocus) boxes.current[0]?.focus(); }, [autoFocus]);
  // A wrong code clears the boxes, ready for the next.
  useEffect(() => {
    if (!disabled && digits.every(Boolean)) {
      setDigits(Array(LENGTH).fill(''));
      boxes.current[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const fill = (start, text) => {
    const chars = String(text).replace(/\D/g, '').slice(0, LENGTH - start).split('');
    if (!chars.length) return;
    const next = [...digits];
    chars.forEach((c, i) => { next[start + i] = c; });
    setDigits(next);
    const end = Math.min(start + chars.length, LENGTH - 1);
    boxes.current[end]?.focus();
    if (next.every(Boolean)) onComplete(next.join(''));
  };

  return (
    <div className="tf-code" role="group" aria-label={label}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { boxes.current[i] = el; }}
          className="tf-code-box"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${i + 1}`}
          maxLength={i === 0 ? LENGTH : 1}
          value={d}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') { const next = [...digits]; next[i] = ''; setDigits(next); return; }
            fill(i, v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus();
            if (e.key === 'ArrowLeft' && i > 0) boxes.current[i - 1]?.focus();
            if (e.key === 'ArrowRight' && i < LENGTH - 1) boxes.current[i + 1]?.focus();
          }}
          onPaste={(e) => { e.preventDefault(); fill(0, e.clipboardData.getData('text')); }}
        />
      ))}
    </div>
  );
}

/** Recovery codes to keep: shown once, to copy, download or print. */
export function RecoveryCodes({ codes, onDone, doneLabel = 'Continue' }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { /* the codes stay on screen to copy by hand */ }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`Kinspeed Wallboard recovery codes\nEach code works once, if you cannot use your authenticator app.\n\n${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kinspeed-wallboard-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tf-recovery">
      <p className="tf-lead">Keep these recovery codes somewhere safe, such as a password manager. Each one signs you in once if you lose your phone. They will not be shown again.</p>
      <ol className="tf-recovery-codes" aria-label="Recovery codes">
        {codes.map((c) => <li key={c}><code>{c}</code></li>)}
      </ol>
      <div className="tf-row">
        <button type="button" className="tf-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
        <button type="button" className="tf-secondary" onClick={download}>Download</button>
      </div>
      <label className="tf-check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I have saved my recovery codes</span>
      </label>
      <button type="button" className="login-btn tf-primary" disabled={!saved} onClick={onDone}>{doneLabel}</button>
    </div>
  );
}

const AppNames = () => <>an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password</>;

/** The first sign-in: scan the QR code, enter a code, then keep the recovery codes. */
function Setup({ challenge, onSignedIn, onCancel }) {
  const [setup, setSetup] = useState(null);
  const [qr, setQr] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    authAPI.loginSetup(challenge)
      .then(async (res) => {
        setSetup(res.data);
        setQr(await QRCode.toDataURL(res.data.otpauthUri, { margin: 1, width: 200, errorCorrectionLevel: 'M' }));
      })
      .catch((err) => setError(describeError(err, 'Could not start the set-up. Sign in again.')));
  }, [challenge]);

  const confirm = async (code) => {
    setBusy(true);
    setError('');
    try {
      const res = await authAPI.loginSetupConfirm(challenge, code);
      setResult(res.data);
    } catch (err) {
      setError(describeError(err, 'That code did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <>
        <Steps current={2} />
        <h4 className="tf-title">Save your recovery codes</h4>
        <RecoveryCodes codes={result.recoveryCodes} onDone={() => onSignedIn(result)} />
      </>
    );
  }
  const key = setup?.secret ? setup.secret.match(/.{1,4}/g).join(' ') : '';
  return (
    <>
      <Steps current={1} />
      <h4 className="tf-title">Set up two-step verification</h4>
      <p className="tf-lead">Every sign-in needs a code from your phone as well as your password. Scan this QR code with <AppNames />.</p>
      {error && <p className="login-error" role="alert">{error}</p>}
      <div className="tf-qr">{qr ? <img src={qr} alt="QR code to scan with your authenticator app" width="200" height="200" /> : <div className="tf-qr-wait">Preparing…</div>}</div>
      {setup && (
        <div className="tf-key">
          {showKey ? (
            <>
              <span>Enter this key in the app instead:</span>
              <code aria-label="Set-up key">{key}</code>
              <button type="button" className="tf-link" onClick={() => navigator.clipboard?.writeText(setup.secret)}>Copy key</button>
            </>
          ) : <button type="button" className="tf-link" onClick={() => setShowKey(true)}>Can&apos;t scan it? Enter a key instead</button>}
        </div>
      )}
      <p className="tf-step-label">Then enter the 6-digit code the app shows</p>
      <CodeInput onComplete={confirm} disabled={busy || !setup} autoFocus={false} />
      <button type="button" className="tf-link tf-back" onClick={onCancel}>Back to sign in</button>
    </>
  );
}

/** Later sign-ins: a code from the app, or a recovery code. */
function Verify({ challenge, onSignedIn, onCancel }) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [low, setLow] = useState(null);

  const submit = async (code) => {
    setBusy(true);
    setError('');
    try {
      const res = await authAPI.loginVerify(challenge, code);
      // After a recovery code, say how many are left before going on.
      if (res.data.usedRecovery) setLow(res.data);
      else onSignedIn(res.data);
    } catch (err) {
      setError(describeError(err, 'That code did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (low) {
    return (
      <>
        <h4 className="tf-title">Signed in with a recovery code</h4>
        <p className="tf-lead">
          You have {low.recoveryCodesLeft} recovery code{low.recoveryCodesLeft === 1 ? '' : 's'} left.
          {low.recoveryCodesLeft <= 3 && ' Create new ones from your profile, under Two-step verification.'}
          {' '}If you lost your phone, ask an admin to reset your two-step verification, and set it up again.
        </p>
        <button type="button" className="login-btn tf-primary" onClick={() => onSignedIn(low)}>Continue</button>
      </>
    );
  }
  return (
    <>
      <h4 className="tf-title">Two-step verification</h4>
      {error && <p className="login-error" role="alert">{error}</p>}
      {useRecovery ? (
        <form onSubmit={(e) => { e.preventDefault(); submit(recovery); }} className="tf-recovery-form">
          <p className="tf-lead">Enter one of the recovery codes you saved when you set up two-step verification.</p>
          <div className="login-field active">
            <input id="recovery-code" value={recovery} autoComplete="off" autoFocus disabled={busy} onChange={(e) => setRecovery(e.target.value)} placeholder="xxxxx-xxxxx" />
            <label htmlFor="recovery-code">Recovery code</label>
          </div>
          <button type="submit" className="login-btn tf-primary" disabled={busy || recovery.trim().length < 10}>Sign in</button>
          <button type="button" className="tf-link" onClick={() => { setUseRecovery(false); setError(''); }}>Use a code from my app instead</button>
        </form>
      ) : (
        <>
          <p className="tf-lead">Enter the 6-digit code from your authenticator app.</p>
          <CodeInput onComplete={submit} disabled={busy} />
          <button type="button" className="tf-link" onClick={() => { setUseRecovery(true); setError(''); }}>Lost your phone? Use a recovery code</button>
        </>
      )}
      <button type="button" className="tf-link tf-back" onClick={onCancel}>Back to sign in</button>
    </>
  );
}

const Steps = ({ current }) => (
  <ol className="tf-steps" aria-label="Set-up steps">
    {['Scan the QR code', 'Save recovery codes'].map((s, i) => (
      <li key={s} className={i + 1 === current ? 'is-current' : i + 1 < current ? 'is-done' : ''} aria-current={i + 1 === current ? 'step' : undefined}>{s}</li>
    ))}
  </ol>
);

/** The second step of signing in: setting up an authenticator the first time, or entering a code. */
export default function TwoFactor({ step, challenge, onSignedIn, onCancel }) {
  return (
    <div className="tf">
      {step === 'setup'
        ? <Setup challenge={challenge} onSignedIn={onSignedIn} onCancel={onCancel} />
        : <Verify challenge={challenge} onSignedIn={onSignedIn} onCancel={onCancel} />}
    </div>
  );
}
